import type { Decoder } from '@simonbackx/simple-encoding';
import { ComponentWithProperties, defineRoute, NavigationController, onCheckRoutes, ReactiveUrl, UrlHelper, useModalStackComponent } from '@simonbackx/vue-app-navigation';
import { AsyncComponent } from '@stamhoofd/components/containers/AsyncComponent.ts';
import { GlobalEventBus } from '@stamhoofd/frontend-application/EventBus.ts';
import { rememberPendingNoltLogin } from '@stamhoofd/components/helpers/nolt.ts';
import { useContext } from '@stamhoofd/components/hooks/useContext.ts';
import { usePlatform } from '@stamhoofd/components/hooks/usePlatform.ts';
import { CenteredMessage } from '@stamhoofd/components/overlays/CenteredMessage.ts';
import { Toast } from '@stamhoofd/components/overlays/Toast.ts';
import type { NavigationActions } from '@stamhoofd/components/types/NavigationActions.ts';
import { NetworkManager } from '@stamhoofd/networking/NetworkManager';
import type { PaymentGeneral, Platform } from '@stamhoofd/structures';
import { EmailAddressSettings, PaymentStatus } from '@stamhoofd/structures';

let didCheckGlobalRoutes = false;

export function useGlobalRoutes() {
    const modalStackComponent = useModalStackComponent();
    const context = useContext();
    const platform = usePlatform();

    defineRoute({
        url: 'reset-password',
        component: async () => (await import('@stamhoofd/components/auth/ForgotPasswordResetView.vue')).default,
        defaultProperties(query?: URLSearchParams | null) {
            return {
                token: query?.get('token') || '',
            };
        },
        propsToParams(props) {
            const query = new URLSearchParams();
            if (props.token) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                query.set('token', (props as any).token);
            }
            return {
                query,
            };
        },
    });

    const present = async (component: ComponentWithProperties) => {
        const currentPath = UrlHelper.shared.getPath({ removeLocale: true });
        await modalStackComponent.value?.present({
            adjustHistory: true,
            animated: false,
            force: true,
            components: [
                new ComponentWithProperties(NavigationController, {
                    root: component,
                }, {
                    provide: {
                        reactive_navigation_url: new ReactiveUrl({ url: currentPath }),
                    },
                }),
            ],
            modalDisplayStyle: 'popup',
        });
    };

    onCheckRoutes(async () => {
        if (didCheckGlobalRoutes) {
            // When switching root components, the routes are rechecked, so we need to prevent rechecks on these routes
            return;
        }
        didCheckGlobalRoutes = true;
        const currentPath = UrlHelper.shared.getPath({ removeLocale: true });
        const parts = UrlHelper.shared.getParts();
        const queryString = UrlHelper.shared.getSearchParams();
        console.log('Checking global routes', parts, queryString, currentPath);

        if (queryString.get('paymentId')) {
            const paymentId = queryString.get('paymentId');
            const organizationId = queryString.get('organizationId');
            const cancel = queryString.get('cancel') === 'true';

            if (paymentId) {
                await present(AsyncComponent(() => import('@stamhoofd/components/payments/PaymentPendingView.vue'), {
                    server: organizationId ? context.value.getOptionalAuthenticatedServerForOrganization(organizationId) : context.value.optionalAuthenticatedServer,
                    paymentId,
                    cancel,
                    errorHandler: async function (navigationActions: NavigationActions, error: unknown) {
                        await navigationActions.dismiss({ force: true });
                        CenteredMessage.fromError(error).addCloseButton().show();
                    },
                    finishedHandler: async function (navigationActions: NavigationActions, payment: PaymentGeneral | null) {
                        GlobalEventBus.sendEvent('paymentPatch', payment).catch(console.error);

                        if (payment && payment.status === PaymentStatus.Succeeded) {
                            await navigationActions.show({
                                components: [
                                    AsyncComponent(() => import('@stamhoofd/components/payments/PaymentSuccessView.vue'), {
                                        payment,
                                    }),
                                ],
                                replace: 100, // autocorrects to all
                                force: true,
                            });
                        } else {
                            await navigationActions.dismiss({ force: true });
                            new CenteredMessage($t(`%Je`), $t(`%Jf`)).addCloseButton().show();
                        }
                    },
                }));
            }
        }

        const initialParts = UrlHelper.initial.getParts();
        const initialQueryString = UrlHelper.initial.getSearchParams();

        if (initialParts.length === 1 && initialParts[0] === 'unsubscribe') {
            const id = initialQueryString.get('id');
            const token = initialQueryString.get('token');
            const type = initialQueryString.get('type') ?? 'all';

            if (id && token && ['all', 'marketing'].includes(type)) {
                await unsubscribe(id, token, type as 'all' | 'marketing', platform.value);
            }
        }

        if (rememberPendingNoltLogin()) {
            new Toast($t('%Zy3'), 'info').setHide(15 * 1000).show();
        }

        await context.value.checkSSO();
    });
}

async function unsubscribe(id: string, token: string, type: 'all' | 'marketing', platform: Platform) {
    const toast = new Toast($t(`%Ib`), 'spinner').setHide(null).show();

    try {
        const response = await NetworkManager.server.request({
            method: 'GET',
            path: '/email/manage',
            query: {
                id,
                token,
            },
            decoder: EmailAddressSettings as Decoder<EmailAddressSettings>,
        });

        const details = response.data;
        toast.hide();

        let doUnsubscribe = true;
        const fieldName = type === 'all' ? 'unsubscribedAll' : 'unsubscribedMarketing';

        if (details[fieldName]) {
            if (!await CenteredMessage.confirm($t(`%Ic`), $t(`%Id`), $t(`%Ie`, { name: details.organization?.name ?? platform.config.name, email: details.email, complaintEmail: $t('%W') }))) {
                return;
            }

            doUnsubscribe = false;
        } else {
            if (!await CenteredMessage.confirm($t(`%If`), $t(`%Ig`), $t(`%Ih`, { name: details.organization?.name ?? platform.config.name, email: details.email }))) {
                return;
            }
            toast.show();
        }

        await NetworkManager.server.request({
            method: 'POST',
            path: '/email/manage',
            body: {
                id,
                token,
                [fieldName]: doUnsubscribe,
            },
        });
        toast.hide();

        if (doUnsubscribe) {
            new Toast($t(`%Ii`), 'success').setHide(15 * 1000).show();
        } else {
            new Toast($t(`%Ij`), 'success').setHide(15 * 1000).show();
        }
    } catch (e) {
        console.error(e);
        toast.hide();
        Toast.fromError(e).show();
    }
}
